/**
 * Detecção de build obsoleta.
 *
 * `REACT_APP_BUILD_ID` é injetado no bundle em tempo de build e o mesmo
 * identificador é publicado em `/version.json`, que nunca deve ser cacheado.
 * Comparar os dois permite descobrir que o navegador (ou um PWA instalado)
 * continua executando um app shell antigo, mesmo sem service worker.
 *
 * A detecção nunca recarrega a página sozinha: o recarregamento descarta o
 * estado em memória da inspeção. A decisão é sempre do operador.
 */

export const APP_BUILD_ID = String(
  process.env.REACT_APP_BUILD_ID ?? "",
).trim();

export const APP_VERSION_MANIFEST_PATH = "/version.json";

export interface AppVersionManifest {
  buildId: string;
}

export function isAppVersionCheckEnabled() {
  // Em testes não há servidor para `version.json`; a checagem só ruidaria.
  if (process.env.NODE_ENV === "test") return false;
  return APP_BUILD_ID.length > 0 && APP_BUILD_ID !== "development";
}

export function isBuildObsolete(
  runningBuildId: string,
  deployedBuildId: string | null,
) {
  if (!runningBuildId || runningBuildId === "development") return false;
  if (!deployedBuildId) return false;
  return deployedBuildId !== runningBuildId;
}

export async function fetchDeployedBuildId(
  signal?: AbortSignal,
): Promise<string | null> {
  if (typeof fetch !== "function") return null;

  try {
    const response = await fetch(
      `${APP_VERSION_MANIFEST_PATH}?t=${Date.now()}`,
      {
        cache: "no-store",
        credentials: "same-origin",
        headers: { "Cache-Control": "no-cache" },
        signal,
      },
    );
    if (!response.ok) return null;
    const payload = (await response.json()) as Partial<AppVersionManifest>;
    const buildId = String(payload?.buildId ?? "").trim();
    return buildId || null;
  } catch {
    // Rede indisponível não é motivo para bloquear o operador.
    return null;
  }
}

/**
 * Recarrega buscando a versão publicada. O `replace` evita empilhar entradas
 * de histórico e o parâmetro só existe para furar caches intermediários de
 * `index.html`; a própria página o remove ao carregar.
 */
export function reloadToLatestBuild() {
  if (typeof window === "undefined") return;
  window.location.reload();
}

const AUTO_UPDATE_ATTEMPT_KEY = "facilita:auto-update-reload-build-id";

/**
 * Evita loop de reload na atualização automática: um recarregamento só é
 * tentado uma vez por build publicada. Se a página recarregar e ainda
 * reportar a mesma build obsoleta (por exemplo, um cache intermediário
 * servindo o `index.html` antigo), a atualização automática não insiste
 * indefinidamente. `sessionStorage` sobrevive ao reload, ao contrário do
 * estado em memória.
 */
export function wasAutoUpdateAttempted(deployedBuildId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.sessionStorage.getItem(AUTO_UPDATE_ATTEMPT_KEY) === deployedBuildId
    );
  } catch {
    return false;
  }
}

export function markAutoUpdateAttempted(deployedBuildId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(AUTO_UPDATE_ATTEMPT_KEY, deployedBuildId);
  } catch {
    // Sem sessionStorage (ex.: modo privado) não há como lembrar a
    // tentativa; na pior hipótese o guard de loop fica desativado.
  }
}

const MAIN_SCRIPT_PATTERN = /\/static\/js\/main\.[0-9a-f]+\.js/;

function runningMainScript(): string | null {
  if (typeof document === "undefined") return null;
  for (const script of Array.from(document.scripts)) {
    const match = script.src.match(MAIN_SCRIPT_PATTERN);
    if (match) return match[0];
  }
  return null;
}

/**
 * Confirma que a build publicada está completa antes de um reload automático.
 *
 * Durante a publicação o servidor pode já expor o `version.json` novo
 * enquanto o `index.html` ou o bundle principal ainda não existem (a build de
 * produção é gerada na própria pasta servida). Recarregar nesse intervalo
 * deixaria o operador numa página vazia ou de erro; nesse caso o reload fica
 * para depois.
 */
export async function isPublishedBuildReady(): Promise<boolean> {
  if (typeof fetch !== "function") return false;
  try {
    const page = await fetch(`/?build-check=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!page.ok) return false;
    const mainScript = (await page.text()).match(MAIN_SCRIPT_PATTERN)?.[0];
    if (!mainScript) return false;
    // `index.html` ainda antigo: recarregar voltaria para esta mesma build e
    // gastaria a única tentativa automática.
    if (mainScript === runningMainScript()) return false;
    const script = await fetch(mainScript, {
      method: "HEAD",
      cache: "no-store",
      credentials: "same-origin",
    });
    return script.ok;
  } catch {
    return false;
  }
}

/** Novas verificações enquanto a publicação não termina (~7,5 min no total). */
export const PUBLISHED_BUILD_RETRY_DELAYS_MS = [
  15_000, 30_000, 60_000, 120_000, 240_000,
];
